// TMS-AUTOSEED-SEAT-F-PARITY-1 (2026-05-28)
// Adds canonical-shape spot_bids to the dev auto-seed so the TMS-C3 chain
// (/tms/spot-bid → AEGIS Top-3 → Award #N → rate-conf → load tendered) works
// end-to-end on a fresh dev/demo boot. Carrier and load IDs match the
// 'carr-XXXXX' and 'load-XXXXX' format produced by seedDataExpanded.js.
//
// Shape matches SpotBidService.createBid contract exactly:
//   id, tenantId, loadId, status, carrierIds[], targetRate, expiresAt,
//   expiresInMinutes, requiresInsuranceCert, responses[], winnerId,
//   winnerRate, awardedAt.
//
// Status distribution skews open so demo operators always find live bids
// (3 open · 1 awarded · 1 expired).

import { DEMO_TENANT_ID } from './tenantDefaults';

const HOUR = 60 * 60 * 1000;
const MINUTE = 60 * 1000;

// Stable seed epoch — avoids "expired since boot" drift in long-running dev sessions
const EPOCH = new Date('2026-05-28T12:00:00Z').getTime();

const mkResponse = (carrierId, rateOffered, target, minsAgo, firstIn = false) => ({
  carrierId,
  rateOffered,
  notes: firstIn ? 'First in — confirm pickup window' : '',
  withinTarget: rateOffered <= target,
  respondedAt: new Date(EPOCH - minsAgo * MINUTE).toISOString(),
});

// Bid 1 — open, 3 responses, all below target
const BID_1_TARGET = 2800;
const BID_1_RESPONSES = [
  mkResponse('carr-00001', 2650, BID_1_TARGET, 8, true),
  mkResponse('carr-00002', 2750, BID_1_TARGET, 5),
  mkResponse('carr-00003', 2900, BID_1_TARGET, 3),
];

// Bid 2 — open, 2 responses
const BID_2_TARGET = 3400;
const BID_2_RESPONSES = [
  mkResponse('carr-00002', 3200, BID_2_TARGET, 12, true),
  mkResponse('carr-00004', 3350, BID_2_TARGET, 7),
];

// Bid 3 — open, 3 responses (tight competition)
const BID_3_TARGET = 2200;
const BID_3_RESPONSES = [
  mkResponse('carr-00003', 2100, BID_3_TARGET, 15, true),
  mkResponse('carr-00005', 2150, BID_3_TARGET, 9),
  mkResponse('carr-00001', 2300, BID_3_TARGET, 4),
];

// Bid 4 — awarded
const BID_4_TARGET = 4100;
const BID_4_RESPONSES = [
  mkResponse('carr-00001', 3900, BID_4_TARGET, 60, true),
  mkResponse('carr-00004', 4050, BID_4_TARGET, 55),
];
const BID_4_WINNER = BID_4_RESPONSES
  .filter(r => r.withinTarget)
  .sort((a, b) => a.rateOffered - b.rateOffered)[0];

const baseBid = (i, fields) => ({
  id: `spot-bid-seed-${String(i).padStart(3, '0')}`,
  tenantId: DEMO_TENANT_ID,
  loadId: fields.loadId,
  status: fields.status,
  carrierIds: fields.carrierIds,
  targetRate: fields.targetRate,
  responses: fields.responses,
  expiresAt: new Date(EPOCH + 30 * MINUTE).toISOString(),
  expiresInMinutes: 30,
  requiresInsuranceCert: i % 2 === 0,
  winnerId: fields.winner ? fields.winner.carrierId : null,
  winnerRate: fields.winner ? fields.winner.rateOffered : null,
  awardedAt: fields.winner
    ? new Date(new Date(fields.winner.respondedAt).getTime() + 2 * MINUTE).toISOString()
    : null,
  createdAt: new Date(EPOCH - (i + 1) * 10 * MINUTE).toISOString(),
  updatedAt: new Date(EPOCH - (i + 1) * 10 * MINUTE).toISOString(),
  createdBy: 'autoseed',
  updatedBy: 'autoseed',
  version: 1,
  source: 'autoseed-seat-f-parity',
});

export const SEED_SPOT_BIDS = [
  baseBid(1, {
    loadId: 'load-00001', status: 'open',
    carrierIds: ['carr-00001', 'carr-00002', 'carr-00003'],
    targetRate: BID_1_TARGET, responses: BID_1_RESPONSES, winner: null,
  }),
  baseBid(2, {
    loadId: 'load-00002', status: 'open',
    carrierIds: ['carr-00002', 'carr-00004', 'carr-00005'],
    targetRate: BID_2_TARGET, responses: BID_2_RESPONSES, winner: null,
  }),
  baseBid(3, {
    loadId: 'load-00003', status: 'open',
    carrierIds: ['carr-00001', 'carr-00002', 'carr-00003', 'carr-00004', 'carr-00005'],
    targetRate: BID_3_TARGET, responses: BID_3_RESPONSES, winner: null,
  }),
  baseBid(4, {
    loadId: 'load-00004', status: 'awarded',
    carrierIds: ['carr-00001', 'carr-00004'],
    targetRate: BID_4_TARGET, responses: BID_4_RESPONSES, winner: BID_4_WINNER,
  }),
  baseBid(5, {
    loadId: 'load-00005', status: 'expired',
    carrierIds: ['carr-00001', 'carr-00002', 'carr-00003'],
    targetRate: 1900, responses: [], winner: null,
  }),
];

export const SEED_SPOT_BIDS_SUMMARY = {
  total: SEED_SPOT_BIDS.length,
  open: SEED_SPOT_BIDS.filter(b => b.status === 'open').length,
  awarded: SEED_SPOT_BIDS.filter(b => b.status === 'awarded').length,
  expired: SEED_SPOT_BIDS.filter(b => b.status === 'expired').length,
  openWithResponses: SEED_SPOT_BIDS.filter(b => b.status === 'open' && b.responses.length > 0).length,
};
